import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { distinctUntilChanged, map } from 'rxjs';
import { processRateOpts } from '../../../shared/utils/functions';

import { ColorItemSchemeClass } from '../../../theme.const';
import { THEME_COLORS, THEME_OPTIONS, THEME_VIEW_ITEMS, LEASE_RATE_OPTS } from './settings.const';
import { LeaseRateSelection, OptimzeScaleOption, Settings, ThemeOption, ThemeSettingsSection } from './settings.model';

@UntilDestroy()
@Component({
  selector: 'sp-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: false,
})
export class SettingComponent implements OnInit, OnDestroy {
  settingsFormGroup: UntypedFormGroup;
  colorSchemeFG: UntypedFormGroup;
  themeColors: ColorItemSchemeClass[] = THEME_COLORS;
  themeOptions: ThemeOption[] = THEME_OPTIONS;
  leaseRateOptions: LeaseRateSelection[] = LEASE_RATE_OPTS;
  colorSchemeSettingsSection: ThemeSettingsSection[] = THEME_VIEW_ITEMS;
  public OptimzeScaleOption = OptimzeScaleOption;
  disableSaveButton = false;

  get isOptimzeScale() {
    const { isOptimzeScale } = this.settingsFormGroup.get('optimizeScale').value;
    return isOptimzeScale;
  }

  constructor(
    public dialogRef: MatDialogRef<SettingComponent>,
    @Inject(MAT_DIALOG_DATA) public settings: Settings,
    private formBuilder: UntypedFormBuilder
  ) {
    this.formInit();
  }

  ngOnDestroy(): void {
    /* required by angular-l10n */
  }

  ngOnInit(): void {
    /* required by angular-l10n */
  }

  /**
   * Initialize a form group object following the {@link Settings} interface
   * structure.
   */
  private formInit() {
    const displayOptions: UntypedFormGroup = this.formBuilder.group({
      showElevators: new UntypedFormControl(this.settings.displayOptions.showElevators),
    });
    const leaseRateCtrl = new UntypedFormControl(this.settings.cardDetails.showLeaseRate);
    const rentDateCtrl = new UntypedFormControl(this.settings.cardDetails.rentDate);
    const leaseRateOptsFC = this.getRateOptsFC(this.settings.cardDetails.leaseRateOpts);
    const includeEstimatedRatesCtrl = new UntypedFormControl(this.settings.cardDetails.includeEstimatedRates);
    const includeEstimatedRateIndicatorCtrl = new UntypedFormControl(
      this.settings.cardDetails.includeEstimatedRateIndicator
    );

    const cardDetails: UntypedFormGroup = this.formBuilder.group({
      suiteNumber: new UntypedFormControl(this.settings.cardDetails.suiteNumber),
      leaseEndingDate: new UntypedFormControl(this.settings.cardDetails.leaseEndingDate),
      spaceSize: new UntypedFormControl(this.settings.cardDetails.spaceSize),
      tenantRepAgency: new UntypedFormControl(this.settings.cardDetails.tenantRepAgency),
      rentDate: rentDateCtrl,
      leaseRateOpts: leaseRateOptsFC,
      showLeaseRate: leaseRateCtrl,
      includeEstimatedRates: includeEstimatedRatesCtrl,
      includeEstimatedRateIndicator: includeEstimatedRateIndicatorCtrl,
    });
    const colorScheme = this.formBuilder.group({
      expiry: new UntypedFormControl(this.settings.colorScheme.expiry),
      industry: new UntypedFormControl(this.settings.colorScheme.industry),
      usage: new UntypedFormControl(this.settings.colorScheme.usage),
      occupier: new UntypedFormControl(this.settings.colorScheme.occupier),
    });
    const optimizeScale = this.formBuilder.group({
      isOptimzeScale: new UntypedFormControl(this.settings.optimizeScale.isOptimzeScale),
      minimunScale: new UntypedFormControl(this.settings.optimizeScale.minimunScale),
    });
    this.settingsFormGroup = this.formBuilder.group({
      displayOptions,
      cardDetails,
      colorScheme,
      optimizeScale,
    });

    leaseRateCtrl.valueChanges
      .pipe(
        untilDestroyed(this),
        map(val => (val === null ? false : val)),
        distinctUntilChanged()
      )
      .subscribe(isRateSelected => {
        this.manageLeaseRateOpts(isRateSelected, leaseRateOptsFC);
      });

    rentDateCtrl.valueChanges
      .pipe(
        untilDestroyed(this),
        map(val => (val === null ? false : val)),
        distinctUntilChanged()
      )
      .subscribe(isAskingRentSelected => {
        this.manageAskingRent(isAskingRentSelected, includeEstimatedRatesCtrl, includeEstimatedRateIndicatorCtrl);
      });

    includeEstimatedRatesCtrl.valueChanges
      .pipe(
        untilDestroyed(this),
        map(val => (val === null ? false : val)),
        distinctUntilChanged()
      )
      .subscribe(isIncludeEstimatedRatesSelected => {
        this.manageIncludeEstimatedRates(isIncludeEstimatedRatesSelected, includeEstimatedRateIndicatorCtrl);
      });

    this.settingsFormGroup.statusChanges.subscribe(status => {
      this.disableSaveButton = status === 'INVALID';
    });
    this.manageLeaseRateOpts(this.settings.cardDetails.showLeaseRate, leaseRateOptsFC);
    this.colorSchemeFG = colorScheme;
  }

  manageAskingRent(
    isAskingRentSelected: boolean,
    includeEstimatedRatesCtrl: FormControl,
    includeEstimatedRateIndicatorCtrl: FormControl
  ) {
    if (!isAskingRentSelected) {
      includeEstimatedRatesCtrl.setValue(false);
      includeEstimatedRateIndicatorCtrl.setValue(false);
    }
  }

  manageIncludeEstimatedRates(
    isIncludeEstimatedRatesSelected: boolean,
    includeEstimatedRateIndicatorCtrl: FormControl
  ) {
    if (!isIncludeEstimatedRatesSelected) {
      includeEstimatedRateIndicatorCtrl.setValue(false);
    }
  }

  manageLeaseRateOpts(isRateSelected: boolean, leaseRateOptsFC: FormControl) {
    if (isRateSelected) {
      leaseRateOptsFC.enable();
      leaseRateOptsFC.setValidators(Validators.required);
      if (!leaseRateOptsFC.value) {
        leaseRateOptsFC.setValue('baseRent');
      }
    } else {
      leaseRateOptsFC.disable();
      leaseRateOptsFC.setValue(null);
      leaseRateOptsFC.setValidators(null);
    }
    leaseRateOptsFC.updateValueAndValidity();
  }

  getRateOptsFC(leaseRateOpts: string) {
    const fcVal = processRateOpts(leaseRateOpts);
    return new UntypedFormControl(fcVal);
  }

  saveSettings() {
    this.dialogRef.close(this.settingsFormGroup.value);
  }
}
